import { Trans, t } from '@lingui/macro';
import { useLingui } from "@lingui/react";
import { PlusCircle, Loader2 } from "lucide-react";
import { FC, useState } from "react";
import { Message } from "../../../models/Message";
import { Room } from "../../../models/Room";
import { RoomItem } from "../../../models/RoomItem";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import { getDisplayName } from "../../../services/Utils/userDisplayName";
import PagecallOpenModal from "./PagecallOpenModal";
import { SectionCard, SectionActionButton, SectionListItem } from "./SectionCard";
import { useToast } from "../../../hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";

export const PanelPagecall: FC<{ room: Room, items: RoomItem[] }> = ({ room, items }) => {
  useLingui();
  const { toast } = useToast();
  const [selectedItem, setSelectedItem] = useState<RoomItem>();
  const [inProgress, setInProgress] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");

  function createPagecallRoom() {
    if (newRoomName) {
      setInProgress(true);
      Fire.item.createPagecallRoom({ name: newRoomName, roomId: room.id, creator: My.user }).then(r => {
        setSelectedItem(r.item);
        let msg = new Message(room, My.user);
        msg.text = newRoomName;
        msg.autoMessage = 'pagecall-created';
        Fire.msg.saveMessage(msg);
        setShowCreateModal(false);
        setNewRoomName("");
      }).catch(e => {
        toast({ title: t`Error`, description: e.message, duration: 2000 });
        console.error(e);
      }).finally(() => {
        setInProgress(false);
      })
    } else {
      toast({ title: t`Warning`, description: t`Please enter a channel name` });
    }
  }

  return (
    <>
      <SectionCard
        title={t`Pagecall`}
        collapsible={false}
        actions={
          <SectionActionButton
            onClick={() => setShowCreateModal(true)}
            icon={inProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
            label=""
          />
        }
      >
        {items.length > 0 ? (
          <div className="flex flex-col">
            {items.map((i) => (
              <SectionListItem
                key={i.id}
                onClick={() => setSelectedItem(i)}
                className="group"
              >
                <span className="flex-1 text-sm text-[color:rgb(var(--text-body))] truncate group-hover:text-primary transition-colors">
                  {i.title}
                </span>
                <span className="text-xs text-muted-foreground">
                  {getDisplayName(i.user)}
                </span>
              </SectionListItem>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic py-3 text-center">
            <Trans>No pagecall channels</Trans>
          </p>
        )}
      </SectionCard>

      {selectedItem && <PagecallOpenModal item={selectedItem} onClose={() => setSelectedItem(undefined)} />}

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><Trans>Create a meeting channel</Trans></DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder={t`Channel name`}
              value={newRoomName}
              onChange={(e) => setNewRoomName(e.target.value)}
              disabled={inProgress}
              onKeyDown={(e) => {
                if (e.key === 'Enter') createPagecallRoom();
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} disabled={inProgress}><Trans>Cancel</Trans></Button>
            <Button onClick={createPagecallRoom} disabled={inProgress || !newRoomName}><Trans>Create</Trans></Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
